import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  DoCheck,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  OnInit,
  Output,
  ViewChild,
  ViewChildren,
  QueryList,
} from '@angular/core';
import { CdkOverlayOrigin, ConnectedOverlayPositionChange, OverlayModule } from '@angular/cdk/overlay';
import { NgTemplateOutlet } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { InputControlBase } from '../../forms/input-control.base';
import { AstralFieldShellComponent } from '../astral-field-shell/astral-field-shell.component';
import { AstralFieldActionComponent } from '../astral-field-action/astral-field-action.component';
import { AstralFieldAccessibilityDirective } from '../astral-field-accessibility.util';
import { SelectionItem } from '../astral-option';
import { resolveAstralIconClass } from '../astral-icon-class.util';
import {
  ASTRAL_DROPDOWN_OVERLAY_VIEWPORT_MARGIN,
  getAstralDropdownOverlayPositions,
  getAstralDropdownOverlayWidth,
} from '../astral-dropdown-overlay.util';
import { LanguageService } from '../../../service/language.service';

@Component({
  selector: 'astral-select',
  standalone: true,
  imports: [NgTemplateOutlet, ReactiveFormsModule, TranslatePipe, OverlayModule, AstralFieldShellComponent, AstralFieldActionComponent, AstralFieldAccessibilityDirective],
  templateUrl: './astral-select.component.html',
  styleUrls: ['./astral-select.component.scss'],
})
export class AstralSelectComponent<TValue = any> extends InputControlBase implements AfterViewInit, DoCheck, OnInit {
  private static nextId = 0;
  private static readonly optionMinHeight = 38;
  private static readonly panelBorderWidth = 2;
  private readonly uid = `astral-select-${++AstralSelectComponent.nextId}`;
  readonly overlayViewportMargin = ASTRAL_DROPDOWN_OVERLAY_VIEWPORT_MARGIN;
  private resizeObserver?: ResizeObserver;
  private interactionDisabled: boolean | undefined;
  private typeaheadBuffer = '';
  private typeaheadResetTimer?: ReturnType<typeof setTimeout>;
  private readonly typeaheadDebounceMs = 200;

  constructor(
    private readonly cd: ChangeDetectorRef,
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly languageService: LanguageService
  ) {
    super();
  }

  @Input() withShell = true;
  @Input() clearOthers = false;
  @Input() filterActions = false;
  @Input() doNotDisplayClear = false;
  @Input() focused = false;
  @Input() inputId: string;
  @Input() inputName: string;
  @Input() itemsSource: SelectionItem<TValue>[];
  @Input() multiple = false;
  @Input() noOptionsPlaceholderText: string;
  @Input() disableWhenNoOptions = true;
  @Input() openAtValue?: TValue;
  @Input() placeholderText: string;
  @Input() span: number | 'full' = 1;
  @Input() supplementalLabel: string;
  @Input() trackBy?: (item: SelectionItem<TValue>) => unknown;
  @Input() visibleOptionCount?: number;

  @Output() readonly addonClick = new EventEmitter();
  @Output() readonly clearClick = new EventEmitter();

  @ViewChild('trigger') trigger: ElementRef<HTMLButtonElement>;
  @ViewChild('panel') panel: ElementRef<HTMLDivElement>;
  @ViewChild('controlShell') controlShell: ElementRef<HTMLDivElement>;
  @ViewChild('overlayOrigin') overlayOrigin: CdkOverlayOrigin;
  @ViewChildren('optionButton') optionButtons: QueryList<ElementRef<HTMLButtonElement>>;

  isFocused = false;
  isOpen = false;
  isPanelAbove = false;
  activeOptionIndex = -1;
  overlayWidth: number;

  @HostBinding('tabindex') tabindex = -1;

  @HostBinding('style.grid-column')
  get hostGridColumn(): string | null {
    if (this.span === 'full') {
      return '1 / -1';
    }

    if (!this.span || this.span <= 1) {
      return null;
    }

    return `span ${this.span}`;
  }

  get resolvedItemsSource(): SelectionItem<TValue>[] {
    return this.itemsSource ?? [];
  }

  get overlayPositions() {
    return getAstralDropdownOverlayPositions();
  }

  get panelMaxHeight(): number | null {
    const optionCount = Math.floor(this.visibleOptionCount ?? 0);
    if (!Number.isFinite(optionCount) || optionCount <= 0) {
      return null;
    }

    return optionCount * AstralSelectComponent.optionMinHeight + AstralSelectComponent.panelBorderWidth;
  }

  get resolvedInputId(): string {
    return this.inputId ?? this.uid;
  }

  get activeOptionId(): string | null {
    return this.activeOptionIndex >= 0 ? this.optionId(this.activeOptionIndex) : null;
  }

  get resolvedInputName(): string {
    if (this.inputName) {
      return this.inputName;
    }

    const caption = this.control?.caption
      ?.toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

    return caption || this.uid;
  }

  get resolvedSupplementalLabel(): string | null {
    return this.supplementalLabel ?? this.control?.supplementalLabel ?? null;
  }

  get resolvedPlaceholderText(): string {
    return this.placeholderText ?? this.control?.caption ?? '';
  }

  get hasOptions(): boolean {
    return this.resolvedItemsSource.length > 0;
  }

  get showNoOptionsPlaceholder(): boolean {
    return !this.hasValue && !this.hasOptions && !!this.resolvedNoOptionsPlaceholderText;
  }

  get resolvedNoOptionsPlaceholderText(): string {
    return this.noOptionsPlaceholderText ?? 'COMMON.NO_OPTIONS_AVAILABLE';
  }

  get resolvedDisabled(): boolean {
    return !!this.control?.disabled || !!this.control?.isReadonly || (this.disableWhenNoOptions && !this.hasOptions);
  }

  get selectedItem(): SelectionItem<TValue> | null {
    return this.resolvedItemsSource.find(item => item.value === this.control?.value) ?? null;
  }

  get selectedItems(): SelectionItem<TValue>[] {
    if (!this.multiple || !Array.isArray(this.control?.value)) {
      return [];
    }

    return this.resolvedItemsSource.filter(item => this.control.value.includes(item.value));
  }

  get triggerLabel(): string {
    if (this.showNoOptionsPlaceholder) {
      return this.resolvedNoOptionsPlaceholderText;
    }

    return this.selectedItem?.name ?? this.resolvedPlaceholderText;
  }

  get selectedItemIcon(): string | null {
    return this.selectedItem?.icon ?? null;
  }

  get displaysPlaceholder(): boolean {
    if (this.showNoOptionsPlaceholder) {
      return true;
    }

    if (this.multiple) {
      return !Array.isArray(this.control?.value) || this.control.value.length === 0;
    }

    return !this.selectedItem;
  }

  get readonlyIconClass(): string {
    return resolveAstralIconClass('lock') ?? '';
  }

  get showClearOthers(): boolean {
    const parentControls = this.control?.parent?.controls;

    if (this.resolvedDisabled || !this.clearOthers || !parentControls) {
      return false;
    }

    return Object.keys(parentControls).length > 1 && (this.filterActions || this.hasValue);
  }

  get showClearThis(): boolean {
    return !this.resolvedDisabled && !this.doNotDisplayClear && (this.filterActions || this.hasValue);
  }

  get hasValue(): boolean {
    const value = this.control?.value;

    if (Array.isArray(value)) {
      return value.length > 0;
    }

    return value !== undefined && value !== null && value !== '';
  }

  @HostListener('focus')
  focusHost() {
    if (this.resolvedDisabled) return;
    this.trigger?.nativeElement.focus();
  }

  @HostListener('document:keydown.escape', ['$event'])
  handleEscape(event: Event): void {
    if (!this.isOpen) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.closePanel();
  }

  @HostListener('window:resize')
  handleWindowResize(): void {
    if (!this.isOpen) {
      return;
    }

    requestAnimationFrame(() => this.syncOverlayWidth());
  }

  override ngOnInit(): void {
    super.ngOnInit();

    this.subscription.add(
      this.control?.valueChanges.subscribe(() => {
        this.cd.detectChanges();
      })
    );
    this.subscription.add(
      this.control?.readonlyStateChanged.subscribe(() => {
        this.cd.detectChanges();
      })
    );
  }

  ngAfterViewInit(): void {
    this.syncOverlayWidth();

    if (typeof ResizeObserver !== 'undefined' && this.controlShell?.nativeElement) {
      this.resizeObserver = new ResizeObserver(() => this.syncOverlayWidth());
      this.resizeObserver.observe(this.controlShell.nativeElement);
    }

    if (this.focused) {
      this.trigger?.nativeElement.focus();
      this.isFocused = true;
      this.cd.detectChanges();
    }
  }

  override ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.resetTypeaheadBuffer();
    super.ngOnDestroy();
  }

  handleBlur() {
    queueMicrotask(() => {
      const activeElement = this.elementRef.nativeElement.ownerDocument?.activeElement as Node | null;
      if (
        activeElement &&
        (activeElement === this.trigger?.nativeElement || this.panel?.nativeElement?.contains(activeElement))
      ) {
        return;
      }

      if (!this.isOpen) {
        this.isFocused = false;
        this.control?.markAsTouched();
        this.onblur();
      }
    });
  }

  handleFocus() {
    if (this.resolvedDisabled) return;
    this.isFocused = true;
    this.onfocus();
  }

  clearAllOtherInputs(): boolean {
    if (this.resolvedDisabled) return false;
    const parentControls = this.control?.parent?.controls;
    if (!parentControls) {
      return false;
    }

    for (const controlName of Object.keys(parentControls)) {
      const currentControl = this.control.parent.get(controlName);
      if (currentControl !== this.control) {
        currentControl.setValue(undefined);
      }
    }

    this.control.parent.markAsDirty();
    this.addonClick.emit(this.control);
    return false;
  }

  clearInput(): boolean {
    if (!this.control || this.resolvedDisabled) {
      return false;
    }

    this.onfocus();
    this.control.setValue(this.multiple ? [] : undefined);

    let form = this.control.parent;
    while (form?.parent) {
      form = form.parent;
    }

    form?.markAsDirty();
    this.onblur();
    this.clearClick.emit(this.control);
    return false;
  }

  togglePanel(): void {
    if (this.resolvedDisabled) {
      return;
    }

    if (this.isOpen) {
      this.closePanel();
      return;
    }

    this.isOpen = true;
    this.isFocused = true;
    this.onfocus();
    this.syncOverlayWidth();
    this.cd.detectChanges();
    queueMicrotask(() => this.alignPanelToSelection());
  }

  handleTriggerKeydown(event: KeyboardEvent): void {
    if (this.resolvedDisabled) {
      return;
    }

    if (event.key === 'Escape' && this.isOpen) {
      event.preventDefault();
      event.stopPropagation();
      this.closePanel();
      return;
    }

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!this.isOpen) {
        this.togglePanel();
      }

      this.moveActiveOption(event.key === 'ArrowDown' ? 1 : -1);
      return;
    }

    if (this.isPrintableTypeaheadKey(event)) {
      event.preventDefault();
      if (!this.isOpen) {
        this.togglePanel();
      }
      this.moveActiveOptionByTypeahead(event.key);
      return;
    }

    if (!this.isOpen) {
      return;
    }

    if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      this.moveActiveOption(event.key === 'Home' ? 1 : -1, true);
      return;
    }

    if ((event.key === 'Enter' || event.key === ' ') && this.activeOptionIndex >= 0) {
      const activeItem = this.resolvedItemsSource[this.activeOptionIndex];
      if (!activeItem || activeItem.disabled) {
        return;
      }

      event.preventDefault();
      this.selectItem(activeItem);
    }
  }

  selectItem(item: SelectionItem<TValue>): void {
    if (item?.disabled || this.resolvedDisabled) {
      return;
    }

    if (this.multiple) {
      const selectedValues = Array.isArray(this.control?.value) ? [...this.control.value] : [];
      const selectedIndex = selectedValues.indexOf(item.value);

      if (selectedIndex >= 0) {
        selectedValues.splice(selectedIndex, 1);
      } else {
        selectedValues.push(item.value);
      }

      this.control?.setValue(selectedValues);
      this.control?.markAsDirty();
      this.control?.markAsTouched();
      return;
    }

    this.control?.setValue(item.value);
    this.control?.markAsDirty();
    this.control?.markAsTouched();
    this.closePanel();
  }

  isSelected(item: SelectionItem<TValue>): boolean {
    if (this.multiple && Array.isArray(this.control?.value)) {
      return this.control.value.includes(item.value);
    }

    return this.control?.value === item.value;
  }

  trackItem = (_index: number, item: SelectionItem<TValue>): unknown => {
    return this.trackBy ? this.trackBy(item) : item;
  };

  optionId(index: number): string {
    return `${this.resolvedInputId}-option-${index}`;
  }

  isFontIcon(icon: string | null | undefined): boolean {
    if (!icon) {
      return false;
    }

    const resolvedIcon = resolveAstralIconClass(icon);
    return resolvedIcon !== null || /\bfa[srlkbdt]?\b|\bfa-[a-z0-9-]+\b/i.test(icon);
  }

  getIconClass(icon: string | null | undefined): string | null {
    if (!icon) {
      return null;
    }

    return resolveAstralIconClass(icon) ?? icon;
  }

  getIconSrc(icon: string | null | undefined): string | null {
    if (!icon) {
      return null;
    }

    // Branded delivery methods use assets while standard actions use the Font Awesome
    // registry. Resolve an asset first, because the registry intentionally preserves
    // unknown values for callers that pass raw Font Awesome class names.
    if (/^(?:assets\/|data:image\/|https?:\/\/)/i.test(icon)) {
      return icon;
    }

    return this.isFontIcon(icon) ? null : icon;
  }

  ngDoCheck(): void {
    const interactionDisabled = this.resolvedDisabled;
    if (this.interactionDisabled === interactionDisabled) return;

    this.interactionDisabled = interactionDisabled;
    if (interactionDisabled) this.closePanel(false, false);
  }

  getSelectionIconClass(item: SelectionItem<TValue>): string {
    return resolveAstralIconClass(this.isSelected(item) ? 'checkboxChecked' : 'checkboxUnchecked') ?? '';
  }

  handleOverlayOutsideClick(event: MouseEvent): void {
    const target = event.target as Node | null;
    if (target && this.elementRef.nativeElement.contains(target)) {
      return;
    }

    this.closePanel(false);
  }

  handleOverlayPositionChange(event: ConnectedOverlayPositionChange): void {
    this.isPanelAbove = event.connectionPair.overlayY === 'bottom';
  }

  private alignPanelToSelection(): void {
    const targetIndex = this.hasValue
      ? this.resolvedItemsSource.findIndex(item => this.isSelected(item))
      : this.resolvedItemsSource.findIndex(item => item.value === this.openAtValue);
    if (targetIndex < 0) {
      this.panel?.nativeElement.scrollTo({ top: 0 });
      return;
    }

    const option = this.optionButtons?.get(targetIndex)?.nativeElement;
    if (!option) {
      return;
    }

    this.panel?.nativeElement.scrollTo({ top: option.offsetTop });
  }

  private closePanel(restoreFocus = true, markTouched = true): void {
    this.isOpen = false;
    this.isFocused = false;
    this.isPanelAbove = false;
    this.activeOptionIndex = -1;
    this.resetTypeaheadBuffer();
    if (markTouched) {
      this.control?.markAsTouched();
    }
    this.onblur();

    if (restoreFocus) {
      this.trigger?.nativeElement.focus();
    }
  }

  private syncOverlayWidth(): void {
    const shellWidth = this.controlShell?.nativeElement.getBoundingClientRect().width;
    const viewportWidth = this.elementRef.nativeElement.ownerDocument?.documentElement?.clientWidth;

    if (!shellWidth) {
      return;
    }

    if (!viewportWidth) {
      this.overlayWidth = shellWidth;
      return;
    }

    this.overlayWidth = getAstralDropdownOverlayWidth(shellWidth, viewportWidth);
  }

  private moveActiveOption(direction: 1 | -1, moveToBoundary = false): void {
    const items = this.resolvedItemsSource;
    if (!items.length) {
      this.activeOptionIndex = -1;
      return;
    }

    let nextIndex: number;
    if (moveToBoundary) {
      nextIndex = direction === 1 ? 0 : items.length - 1;
    } else if (this.activeOptionIndex >= 0) {
      nextIndex = this.activeOptionIndex;
    } else {
      nextIndex = direction === 1 ? -1 : 0;
    }
    for (let checked = 0; checked < items.length; checked += 1) {
      if (!moveToBoundary || checked > 0) {
        nextIndex = (nextIndex + direction + items.length) % items.length;
      }

      if (!items[nextIndex]?.disabled) {
        this.activeOptionIndex = nextIndex;
        requestAnimationFrame(() => this.scrollActiveOptionIntoView());
        return;
      }
    }
  }

  private scrollActiveOptionIntoView(): void {
    this.optionButtons?.get(this.activeOptionIndex)?.nativeElement.scrollIntoView({ block: 'nearest' });
  }

  private isPrintableTypeaheadKey(event: KeyboardEvent): boolean {
    return !event.isComposing && !event.ctrlKey && !event.metaKey && !event.altKey && event.key.length === 1 && event.key !== ' ';
  }

  private moveActiveOptionByTypeahead(key: string): void {
    clearTimeout(this.typeaheadResetTimer);

    const normalizedKey = this.normalizeTypeaheadText(key);
    const repeatedCharacterSearch =
      !!this.typeaheadBuffer && [...this.typeaheadBuffer].every(character => character === normalizedKey);
    this.typeaheadBuffer += normalizedKey;

    const searchText = repeatedCharacterSearch ? normalizedKey : this.typeaheadBuffer;
    const items = this.resolvedItemsSource;
    const startIndex = repeatedCharacterSearch && this.activeOptionIndex >= 0 ? this.activeOptionIndex + 1 : 0;

    for (let offset = 0; offset < items.length; offset += 1) {
      const index = (startIndex + offset) % items.length;
      const item = items[index];
      if (item.disabled) {
        continue;
      }

      const translatedName = String(this.languageService.instant(item.name) ?? item.name);
      if (this.normalizeTypeaheadText(translatedName).startsWith(searchText)) {
        this.activeOptionIndex = index;
        requestAnimationFrame(() => this.scrollActiveOptionIntoView());
        break;
      }
    }

    this.typeaheadResetTimer = setTimeout(() => this.resetTypeaheadBuffer(), this.typeaheadDebounceMs);
  }

  private normalizeTypeaheadText(value: string): string {
    return value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLocaleLowerCase()
      .trimStart();
  }

  private resetTypeaheadBuffer(): void {
    clearTimeout(this.typeaheadResetTimer);
    this.typeaheadResetTimer = undefined;
    this.typeaheadBuffer = '';
  }
}
